import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { devLog } from '../utils/devLog';
import { reconcileStoredToolSelection } from '../utils/toolIds';

const AI_API_PREFIX = 'api/catalog';

/**
 * MCP tools catalog + selection state, extracted out of App.jsx (DDD
 * refactor Phase 2 — see /Users/giulioroggero/.claude/plans/
 * tender-roaming-bentley.md). Pure move: same state, same effects, same
 * behavior.
 *
 * `customAIInstructionsPlaybookData` and `playbookToolIdsRef` are owned by
 * App.jsx's playbook-application logic (a Phase-3 concern, not yet
 * extracted) — taken here as explicit parameters per the plan's own
 * instruction, not a closure reach-back into App.jsx internals. Callers
 * outside this hook (`handleApplyPlaybook`-style code still living in
 * App.jsx) continue to read/write `availableToolsRef`/`selectedToolsRef`/
 * `miaFlowToolIdsRef` directly via the refs this hook returns — refs are
 * stable object identities, so this is unchanged behavior, not a new
 * coupling.
 */
export function useMcpTools(customAIInstructionsPlaybookData, playbookToolIdsRef) {
    const { authorizedFetch } = useAuth();

    const availableToolsRef = useRef([]); // Always-current mirror of availableTools for stale-closure-safe access
    const selectedToolsRef = useRef(null); // Always-current mirror of selectedTools for stale-closure-safe access
    const miaFlowToolIdsRef = useRef([]); // Latest computed Mia Flow Tools default IDs

    const [availableTools, setAvailableTools] = useState([]);
    const [toolsLoadError, setToolsLoadError] = useState(null);
    const [toolsAuthRequired, setToolsAuthRequired] = useState(false);
    const [selectedTools, setSelectedTools] = useState(() => {
        // Load selected tools from localStorage
        try {
            const saved = localStorage.getItem('selectedTools');
            if (saved) {
                return JSON.parse(saved);
            }
            // If no saved selection (first login), return null to trigger default selection after tools load
            return null;
        } catch {
            return null;
        }
    });
    const [expandedCategories, setExpandedCategories] = useState(() => {
        // Load expanded categories from localStorage, default Mia Flow Tools expanded
        try {
            const saved = localStorage.getItem('expandedToolCategories');
            if (saved) {
                return JSON.parse(saved);
            }
            // Default: expand Mia Flow Tools
            return { 'Mia Flow Tools': true };
        } catch {
            return { 'Mia Flow Tools': true };
        }
    });

    // Track tools that were saved with the current session
    const [sessionTools, setSessionTools] = useState(null);
    const [showToolChangeNotice, setShowToolChangeNotice] = useState(false);
    const toolsModalOpenRef = useRef(null);  // Track tools when modal opens for comparison

    // Fetch available MCP tools on mount
    useEffect(() => {
        const fetchTools = async () => {
            try {
                // Scope comes from x-mia-acl-context (getBffHeaders), not a tenantId query param.
                const baseUrl = config.bffUrl || window.location.origin;
                const response = await authorizedFetch(`${baseUrl}/${AI_API_PREFIX}/ai-tools`, {
                    headers: getBffHeaders()
                });

                if (!response.ok) {
                    console.error('❌ Failed to fetch tools, status:', response.status);
                    setToolsLoadError(`HTTP ${response.status}`);
                    return;
                }

                const data = await response.json();
                if (!data.success || !data.data?.items) {
                    console.warn('⚠️ Failed to load tools:', data.error || 'Unknown error');
                    setToolsLoadError(data.details || data.error || 'Failed to load tools');
                    return;
                }

                const allItems = data.data.items;
                devLog('📦 Catalog Tools — fetched', allItems.length, 'tools');
                setAvailableTools(allItems);
                setToolsLoadError(null);
                setToolsAuthRequired(false);

                // Rimuovi dai selected tools quelli che sono disabilitati o che non esistono più (ID format mismatch)
                setSelectedTools(prev => {
                    if (!prev) return prev;
                    // Skip filtering/reset if a playbook is being applied via URL - applyPlaybook will handle tool selection
                    const urlPlaybookId = new URLSearchParams(window.location.search).get('playbookId');
                    if (urlPlaybookId) return prev;
                    // Migrate the stored references to today's ids (an old format, or a playbook's tool names saved
                    // before the list loaded) and drop disabled tools. Only when nothing in it is a tool anymore is
                    // it reset (null): the default-selection effect below then picks and persists the defaults, so
                    // the next load finds a valid selection.
                    const reconciled = reconcileStoredToolSelection(prev, allItems);
                    if (reconciled === null) {
                        devLog('🔧 Stored tool selection matches no tool anymore, using the defaults');
                    }
                    return reconciled;
                });
            } catch (error) {
                console.error('❌ Error fetching catalog tools:', error);
                setToolsLoadError(error.message || 'Network error');
            }
        };

        fetchTools();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep availableToolsRef in sync so callbacks with empty dep arrays can always read the latest list
    useEffect(() => { availableToolsRef.current = availableTools; }, [availableTools]);

    // Keep selectedToolsRef in sync so handleSendMessage always reads the latest selection
    useEffect(() => { selectedToolsRef.current = selectedTools; }, [selectedTools]);

    // Set default tool selection: always ensure Mia Flow Tools are selected for each new browser session
    useEffect(() => {
        if (availableTools.length === 0) return;

        const miaFlowToolIds = availableTools
            .filter(tool => tool.enabled !== false && tool.type === 'built-in' && tool.application === 'mia-flow' && tool.category === 'Mia Flow Tools')
            .map(tool => tool.id);
        miaFlowToolIdsRef.current = miaFlowToolIds;

        // Normalize stale/mismatched tool IDs using the now-available tool list.
        // Strips any legacy "category:name" prefix and handles name→id migration.
        const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
        const normalizeIds = (ids) => {
            if (!ids || ids.length === 0) return ids;
            const knownIds = new Set(availableTools.map(t => t.id));
            const nameToId = new Map(availableTools.map(t => [t.name, t.id]));
            if (ids.every(id => knownIds.has(id))) return ids; // all valid, no-op
            return [...new Set(ids.flatMap(rawId => {
                if (knownIds.has(rawId)) return [rawId];
                // Map from display name (old format) to id
                if (nameToId.has(rawId)) return [nameToId.get(rawId)];
                // Strip legacy "category:name" format
                const sep = rawId.indexOf(':');
                const stripped = sep >= 0 ? rawId.substring(sep + 1) : rawId;
                if (knownIds.has(stripped)) return [stripped];
                if (nameToId.has(stripped)) return [nameToId.get(stripped)];
                // Try kebab-case variant (e.g. snake_case from old ADK)
                const kebab = toKebab(stripped);
                if (knownIds.has(kebab)) return [kebab];
                return []; // unknown ID — drop it
            }))];
        };

        const sessionApplied = sessionStorage.getItem('miaFlowToolsDefaultApplied');

        if (!sessionApplied) {
            // New browser session: normalize + merge Mia Flow Tools into whatever was persisted
            setSelectedTools(prev => {
                const base = normalizeIds(prev || []);
                const merged = [...new Set([...miaFlowToolIds, ...base])];
                devLog('🎯 New session - ensuring Mia Flow Tools selected:', miaFlowToolIds.length, 'tools added to selection');
                return merged;
            });
            sessionStorage.setItem('miaFlowToolsDefaultApplied', '1');
        } else if (selectedTools === null) {
            // Tools loaded but no selection at all (edge case): fall back to Mia Flow Tools
            devLog('🎯 No selection found - defaulting to Mia Flow Tools:', miaFlowToolIds.length, 'tools');
            setSelectedTools(miaFlowToolIds);
        } else {
            // Re-normalize any stale IDs (e.g. from a playbook applied before tools finished loading)
            setSelectedTools(prev => normalizeIds(prev));
        }
    }, [availableTools]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist selected tools to localStorage
    useEffect(() => {
        // Only persist if selectedTools is not null (i.e., it's been initialized)
        if (selectedTools !== null) {
            localStorage.setItem('selectedTools', JSON.stringify(selectedTools));
        }
    }, [selectedTools]);

    // Re-resolve tool IDs when availableTools becomes available while a playbook is active.
    // This handles the timing case where the playbook was applied (or loaded from a saved
    // conversation) before the tools list was fetched from the server, causing resolveToolIds
    // to fall back to storing the raw catalog names (e.g. "execute-bash-script") instead of
    // the canonical tool names used by the UI.
    useEffect(() => {
        if (!customAIInstructionsPlaybookData || availableTools.length === 0) return;
        const knownIds = new Set(availableTools.map(t => t.id));
        const nameToId = new Map(availableTools.map(t => [t.name, t.id]));
        const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
        const resolveIds = (ids) => [...new Set(ids.flatMap(rawId => {
            if (knownIds.has(rawId)) return [rawId];
            if (nameToId.has(rawId)) return [nameToId.get(rawId)];
            const colonIdx = rawId.indexOf(':');
            const stripped = colonIdx >= 0 ? rawId.substring(colonIdx + 1) : rawId;
            if (knownIds.has(stripped)) return [stripped];
            if (nameToId.has(stripped)) return [nameToId.get(stripped)];
            const kebab = toKebab(stripped);
            if (knownIds.has(kebab)) return [kebab];
            const kebabRaw = toKebab(rawId);
            if (knownIds.has(kebabRaw)) return [kebabRaw];
            return [];
        }))];
        // Also re-resolve the playbook-locked IDs so isPlaybookLocked remains accurate
        if (playbookToolIdsRef.current.length > 0) {
            playbookToolIdsRef.current = resolveIds(playbookToolIdsRef.current);
        }
        setSelectedTools(prev => {
            if (!prev || prev.length === 0) return prev;
            const resolved = resolveIds(prev);
            // Skip state update if nothing changed
            if (resolved.length === prev.length && resolved.every((id, i) => id === prev[i])) return prev;
            return resolved;
        });
    }, [availableTools, customAIInstructionsPlaybookData, playbookToolIdsRef]);

    // Detect when tools change after session has started
    useEffect(() => {
        if (sessionTools && sessionTools.length > 0 && selectedTools) {
            // Check if current selection is different from session tools
            const currentSet = new Set(selectedTools);
            const sessionSet = new Set(sessionTools);

            const isDifferent =
                currentSet.size !== sessionSet.size ||
                ![...currentSet].every(tool => sessionSet.has(tool));

            if (isDifferent) {
                setShowToolChangeNotice(true);
            } else {
                setShowToolChangeNotice(false);
            }
        }
    }, [selectedTools, sessionTools]);

    // Persist expanded categories to localStorage
    useEffect(() => {
        localStorage.setItem('expandedToolCategories', JSON.stringify(expandedCategories));
    }, [expandedCategories]);

    // Toggle category expansion
    const toggleCategoryExpansion = useCallback((category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    }, []);

    return {
        availableTools, setAvailableTools,
        toolsLoadError, setToolsLoadError,
        toolsAuthRequired, setToolsAuthRequired,
        selectedTools, setSelectedTools,
        expandedCategories, setExpandedCategories,
        sessionTools, setSessionTools,
        showToolChangeNotice, setShowToolChangeNotice,
        toolsModalOpenRef,
        availableToolsRef,
        selectedToolsRef,
        miaFlowToolIdsRef,
        toggleCategoryExpansion,
    };
}
